import { createColumnHelper } from '@tanstack/react-table';
import { t } from 'i18next';
import {
  ArrowDownIcon,
  ArrowUpIcon,
  ChevronsUpDownIcon,
  TagsIcon,
  CalculatorIcon,
  MailIcon,
  ToggleLeftIcon,
  CalendarIcon,
  TypeIcon,
  PhoneIcon,
} from 'lucide-react';

import { NumbersIcon } from '@/components/atoms/Icons/Numbers';
import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { RowActionIndicator } from '@/components/atoms/RowActionIndicator/RowActionIndicator';
import TimestampTooltip from '@/components/atoms/TimestampTooltip/TimestampTooltip';
import { cn } from '@/lib/utils';
import { SortDirection } from '@/modules/insights/contexts/InsightFilterContext';
import { Insight } from '@/shared/types/insights';
import { COLUMN_PRESETS } from '@/util/constants';
import { timeFromNow } from '@/util/dateUtils';

import ActionsCell from './ActionsCell';

// Insight types constants
const INSIGHT_TYPES = {
  CATEGORICAL: 'categorical',
  INTEGER: 'int',
  NUMBER: 'number',
  EMAIL: 'email',
  BOOLEAN: 'boolean',
  DATETIME: 'datetime',
  STRING: 'string',
  PHONE_NUMBER: 'phone_number',
} as const;

interface ColumnProps {
  typeDirection: SortDirection | null;
  nameDirection: SortDirection | null;
  updatedDirection: SortDirection | null;
  toggleTypeDirection: () => void;
  toggleNameDirection: () => void;
  toggleUpdatedDirection: () => void;
  onEdit: (insightId: string) => void;
  onDuplicate: (insightId: string) => void;
  onDelete: (insightId: string) => void;
  readonly?: boolean;
}

const columnHelper = createColumnHelper<Insight>();

/**
 * Get the appropriate icon for an insight type
 * @param type - The insight type
 * @returns JSX element representing the type icon
 */
const getTypeIcon = (type: string) => {
  switch (type) {
    case INSIGHT_TYPES.CATEGORICAL:
      return <TagsIcon size={16} />;
    case INSIGHT_TYPES.INTEGER:
      return <NumbersIcon size={16} />;
    case INSIGHT_TYPES.NUMBER:
      return <CalculatorIcon size={16} />;
    case INSIGHT_TYPES.EMAIL:
      return <MailIcon size={16} />;
    case INSIGHT_TYPES.BOOLEAN:
      return <ToggleLeftIcon size={16} />;
    case INSIGHT_TYPES.DATETIME:
      return <CalendarIcon size={16} />;
    case INSIGHT_TYPES.STRING:
      return <TypeIcon size={16} />;
    case INSIGHT_TYPES.PHONE_NUMBER:
      return <PhoneIcon size={16} />;
    default:
      return <TypeIcon size={16} />;
  }
};

/**
 * Defines the columns for the insights table
 */
export const columns = ({
  typeDirection,
  nameDirection,
  updatedDirection,
  toggleTypeDirection,
  toggleNameDirection,
  toggleUpdatedDirection,
  onEdit,
  onDuplicate,
  onDelete,
  readonly = false,
}: ColumnProps) => [
  columnHelper.accessor('type', {
    header: () => (
      <button
        type="button"
        onClick={toggleTypeDirection}
        className={cn(
          'inline-flex items-center gap-2',
          'text-sm font-medium whitespace-nowrap text-muted-foreground',
          'transition-colors hover:text-foreground',
          'rounded-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:outline-none'
        )}
        aria-label={t('table.sort_by', { 0: t('common.type') })}
      >
        {t('common.type')}
        {typeDirection === 'desc' ? (
          <ArrowDownIcon size={16} aria-hidden="true" />
        ) : typeDirection === 'asc' ? (
          <ArrowUpIcon size={16} aria-hidden="true" />
        ) : (
          <ChevronsUpDownIcon size={16} aria-hidden="true" />
        )}
      </button>
    ),
    cell: (info) => {
      const type = info.getValue();
      return (
        <InfoTooltip content={t(`insights.${type.toLowerCase()}`)} delayDuration={0} side="top">
          <span className="flex items-center justify-center text-muted-foreground">
            {getTypeIcon(type)}
          </span>
        </InfoTooltip>
      );
    },
    ...COLUMN_PRESETS.SM,
    meta: { preset: 'SM', className: 'pl-4' },
  }),
  columnHelper.accessor('name', {
    header: () => (
      <button
        type="button"
        onClick={toggleNameDirection}
        className={cn(
          'inline-flex items-center gap-2 pl-2',
          'text-sm font-medium whitespace-nowrap text-muted-foreground',
          'transition-colors hover:text-foreground',
          'rounded-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:outline-none'
        )}
        aria-label={t('table.sort_by', { 0: t('common.name') })}
      >
        {t('common.name')}
        {nameDirection === 'desc' ? (
          <ArrowDownIcon size={16} aria-hidden="true" />
        ) : nameDirection === 'asc' ? (
          <ArrowUpIcon size={16} aria-hidden="true" />
        ) : (
          <ChevronsUpDownIcon size={16} aria-hidden="true" />
        )}
      </button>
    ),
    cell: (info) => {
      const name = info.getValue();
      return (
        <div className="flex items-center gap-3 pl-2">
          <span className="min-w-0 truncate font-medium">{name}</span>
          <RowActionIndicator action="edit" />
        </div>
      );
    },
    ...COLUMN_PRESETS.LG,
    meta: { preset: 'LG' },
  }),
  columnHelper.accessor('description', {
    header: () => 'Guideline',
    cell: (info) => {
      const description = info.getValue();
      return <span className="block truncate text-foreground">{description || '-'}</span>;
    },
    ...COLUMN_PRESETS.FLEX,
    meta: { preset: 'FLEX' },
  }),
  columnHelper.accessor('updated', {
    header: () => (
      <button
        type="button"
        onClick={toggleUpdatedDirection}
        className={cn(
          'inline-flex items-center gap-2',
          'text-sm font-medium whitespace-nowrap text-muted-foreground',
          'transition-colors hover:text-foreground',
          'rounded-sm focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:outline-none'
        )}
        aria-label={t('table.sort_by', { 0: t('common.updated') })}
      >
        {t('common.updated')}
        {updatedDirection === 'desc' ? (
          <ArrowDownIcon size={16} aria-hidden="true" />
        ) : updatedDirection === 'asc' ? (
          <ArrowUpIcon size={16} aria-hidden="true" />
        ) : (
          <ChevronsUpDownIcon size={16} aria-hidden="true" />
        )}
      </button>
    ),
    cell: (info) => {
      const updated = info.getValue();
      return (
        <div className="flex items-center gap-2">
          {updated ? (
            <TimestampTooltip timestamp={updated}>{timeFromNow(updated)}</TimestampTooltip>
          ) : (
            '-'
          )}
        </div>
      );
    },
    ...COLUMN_PRESETS.MD,
    meta: { preset: 'MD' },
  }),
  columnHelper.display({
    id: 'actions',
    header: () => '',
    cell: (info) => {
      return (
        <ActionsCell
          insightId={info.row.original.insight_id}
          readonly={readonly}
          onEdit={onEdit}
          onDuplicate={onDuplicate}
          onDelete={onDelete}
        />
      );
    },
    ...COLUMN_PRESETS.SM,
    meta: { preset: 'SM' },
  }),
];
